import { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, FolderIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { FileEntry } from '@/lib/api/generated/model';
import { useWorkspaceFileListing } from '../hooks/useWorkspaceFileListing';
import { canDrop, destinationFor } from '../moveTargets';

/**
 * One expandable folder in the destination tree.
 *
 * Its contents are fetched when it is opened and not before (FR-021a): the
 * service lists one folder at a time and there is deliberately no way to ask
 * for the whole tree at once. A folder already browsed in the listing is
 * already cached, because this is the same query the listing itself uses.
 */
function FolderNode({
  workspaceId,
  path,
  label,
  depth,
  moving,
  selectedPath,
  onSelect,
}: {
  workspaceId: string;
  path: string;
  label: string;
  depth: number;
  moving: FileEntry;
  selectedPath: string;
  onSelect: (path: string) => void;
}) {
  const [expanded, setExpanded] = useState(depth === 0);
  const { listing, isPending } = useWorkspaceFileListing(
    workspaceId,
    expanded ? path : '',
  );

  const folders =
    expanded && listing?.path === path
      ? listing.entries.filter((entry) => entry.kind === 'directory')
      : [];
  const loading = expanded && (isPending || listing?.path !== path);
  const selectable = canDrop(moving, { kind: 'folder', path });

  return (
    <li>
      <div
        className="flex items-center gap-1"
        style={{ paddingLeft: `${String(depth * 12)}px` }}
      >
        <button
          type="button"
          aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
          aria-expanded={expanded}
          onClick={() => {
            setExpanded((previous) => !previous);
          }}
          className="text-muted-foreground hover:text-foreground rounded p-0.5"
        >
          {expanded ? (
            <ChevronDownIcon aria-hidden="true" className="size-4" />
          ) : (
            <ChevronRightIcon aria-hidden="true" className="size-4" />
          )}
        </button>
        <button
          type="button"
          disabled={!selectable}
          aria-current={selectedPath === path ? 'true' : undefined}
          onClick={() => {
            onSelect(path);
          }}
          className={cn(
            'hover:bg-accent flex flex-1 items-center gap-2 rounded px-2 py-1 text-left text-sm disabled:opacity-50',
            selectedPath === path && 'bg-accent font-medium',
          )}
        >
          <FolderIcon aria-hidden="true" className="size-4 shrink-0" />
          <span className="truncate">{label}</span>
        </button>
      </div>

      {expanded && (
        <ul>
          {loading ? (
            <li
              className="text-muted-foreground px-2 py-1 text-xs"
              style={{ paddingLeft: `${String((depth + 1) * 12 + 8)}px` }}
            >
              Loading…
            </li>
          ) : folders.length === 0 ? (
            // Distinct from "Loading…" on purpose (FR-021b) — sharing one
            // blank state would make an empty folder read as a stuck one.
            <li
              className="text-muted-foreground px-2 py-1 text-xs italic"
              style={{ paddingLeft: `${String((depth + 1) * 12 + 8)}px` }}
            >
              No folders inside
            </li>
          ) : (
            folders.map((folder) => (
              <FolderNode
                key={folder.path}
                workspaceId={workspaceId}
                path={folder.path}
                label={folder.name}
                depth={depth + 1}
                moving={moving}
                selectedPath={selectedPath}
                onSelect={onSelect}
              />
            ))
          )}
        </ul>
      )}
    </li>
  );
}

/**
 * Chooses where an item should go.
 *
 * The path that works for everyone: it needs no pointer, and — unlike dragging
 * — it reaches destinations that are not on screen beside the item, which in a
 * long listing is most of them. Dragging is the shortcut; this is the
 * capability.
 */
export function MoveToDialog({
  open,
  workspaceId,
  moving,
  onMove,
  onCancel,
}: {
  open: boolean;
  workspaceId: string;
  moving: FileEntry | undefined;
  onMove: (destinationPath: string) => void;
  onCancel: () => void;
}) {
  const [selectedPath, setSelectedPath] = useState('');

  if (moving === undefined) return null;

  const valid = canDrop(moving, { kind: 'folder', path: selectedPath });

  function close() {
    setSelectedPath('');
    onCancel();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Move “{moving.name}”</DialogTitle>
          <DialogDescription>
            Choose the folder to move it into.
          </DialogDescription>
        </DialogHeader>

        <ul className="max-h-72 overflow-auto" aria-label="Destination folders">
          <FolderNode
            workspaceId={workspaceId}
            path=""
            label="Files"
            depth={0}
            moving={moving}
            selectedPath={selectedPath}
            onSelect={setSelectedPath}
          />
        </ul>

        <DialogFooter>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            disabled={!valid}
            onClick={() => {
              const destination = destinationFor(moving, {
                kind: 'folder',
                path: selectedPath,
              });
              setSelectedPath('');
              onMove(destination);
            }}
          >
            Move here
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
